import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ChevronDown } from 'lucide-react';
import {
  updateTenantUser,
  selectTenantMutating,
  selectTenantMutateError,
} from '../../../store/adminTenantsSlice';

const USER_ROLES = ['admin', 'user'];

export default function EditUserModal({ tenant, onClose, onSuccess }) {
  const dispatch = useDispatch();
  const mutating = useSelector(selectTenantMutating);
  const mutateError = useSelector(selectTenantMutateError);

  const [email] = useState(tenant.email);
  const [userRole, setUserRole] = useState(tenant.userRole);
  const [roleDropdownOpen, setRoleDropdownOpen] = useState(false);
  const [localError, setLocalError] = useState(null);

  const handleProceed = async () => {
    setLocalError(null);
    if (!tenant.userId) {
      setLocalError('Missing user id — cannot update');
      return;
    }

    const result = await dispatch(
      updateTenantUser({
        userId: tenant.userId,
        updates: {
          userRole,
          // Email is intentionally not sent — Phase 1 does not support email changes
        },
      }),
    );

    if (updateTenantUser.fulfilled.match(result)) {
      onSuccess?.();
      onClose();
    } else {
      setLocalError(
        typeof result.payload === 'string'
          ? result.payload
          : 'Failed to update user',
      );
    }
  };

  return (
    <div
      className="fixed inset-0 z-[1100] flex items-center justify-center bg-black/30"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl shadow-lg w-[560px] p-10 flex flex-col gap-6"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className="text-xl font-semibold text-[#1D2739] text-center">Edit User</h2>

        {/* Email — read-only in Phase 1 (identity/auth) */}
        <div className="flex flex-col gap-2">
          <label className="text-base text-[#1D2739] text-start">Email</label>
          <input
            type="email"
            value={email}
            readOnly
            className="w-full bg-[#F4F4F4] rounded-lg px-4 py-3 text-base text-[#1D2739] outline-none opacity-80 cursor-not-allowed"
          />
        </div>

        {/* User Role */}
        <div className="flex flex-col gap-2">
          <label className="text-base text-[#1D2739] text-start">User role</label>
          <div className="relative">
            <button
              type="button"
              onClick={() => setRoleDropdownOpen((o) => !o)}
              className="w-full flex items-center justify-between bg-[#F4F4F4] rounded-lg px-4 py-3 text-base text-[#1D2739]"
            >
              <span>{userRole}</span>
              <ChevronDown className="w-4 h-4 text-[#7E7E7E]" />
            </button>
            {roleDropdownOpen && (
              <div className="absolute z-10 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg">
                {USER_ROLES.map((role) => (
                  <button
                    key={role}
                    type="button"
                    onClick={() => { setUserRole(role); setRoleDropdownOpen(false); }}
                    className="w-full text-left px-4 py-3 text-base text-[#1D2739] hover:bg-gray-50 first:rounded-t-lg last:rounded-b-lg"
                  >
                    {role}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {(localError || mutateError) && (
          <p className="text-sm text-red-600 text-center">
            {localError || mutateError}
          </p>
        )}

        {/* Actions */}
        <div className="flex flex-col gap-3 mt-2">
          <button
            onClick={handleProceed}
            disabled={mutating}
            className="w-full py-3 text-base font-semibold text-[#1D2739] bg-[#F5E424] rounded-full hover:bg-[#E0D020] disabled:opacity-60"
          >
            {mutating ? 'Saving…' : 'Proceed'}
          </button>
          <button
            onClick={onClose}
            disabled={mutating}
            className="w-full py-3 text-base text-[#7E7E7E] hover:text-[#1D2739]"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}
